import { OpenapiProxyStaffSportsGradesResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_sports_grades';
import { encodeGradeForView } from 'src/domains/sportsbook/trading/sdk/utils/encodeGradeForView';
import { OpenapiProxyStaffUpdateSportGradesParamsType } from 'src/api_openapi/generated/openapi_proxy_staff_update_sport_grades';
import {
    TradingMarketBetLimitsLegacyViewType,
    TradingMarketBetLimitsLegacyViewZod,
    TradingMarketBetLimitsViewType,
} from 'src/api_openapi/v2/trading/schemas';
import { ApiSportGradesResponse200Type } from 'src/api_openapi/v2/trading/apiSportGrades';
import { ApiSportGradesLegacyResponse200Type } from 'src/api_openapi/v2/trading/apiSportGradesLegacy';

export type GradeType = OpenapiProxyStaffSportsGradesResponse200Type[number];

type MarketLimitsNewTradingType = Array<TradingMarketBetLimitsViewType>;
type MatchingMarketsNewTradingType = MarketLimitsNewTradingType[number]['matchingMarkets'];
type LegacyMarketLimitsType = TradingMarketBetLimitsLegacyViewType;

function buildMarketLimitsFromNewTrading(
    marketLimits: MarketLimitsNewTradingType | undefined,
    legacyMarketLimits: Array<LegacyMarketLimitsType> | null | undefined
): GradeType['marketLimits'] {
    const outModel: GradeType['marketLimits'] = {};

    if (marketLimits === undefined) {
        console.error('buildMarketLimitsFromNewTrading => marketLimits is null or undefined');
        return {};
    }
    for (const marketLimit of marketLimits) {
        const newTradingLimit = legacyMarketLimits?.filter((item) => {
            const matchingMarketsFromLegacy = item.matchingMarkets;
            const matchingMarkets = marketLimit.matchingMarkets;

            return JSON.stringify(matchingMarketsFromLegacy) === JSON.stringify(matchingMarkets);
        });

        const marketTemplate = newTradingLimit === undefined ? undefined : newTradingLimit[0];
        if (marketTemplate === undefined) {
            outModel['Empty matching markets".to_string()'] = marketLimit.betLimits;
        } else {
            const name = marketTemplate.internalId;
            outModel[name] = marketLimit.betLimits;
        }
    }

    return outModel;
}

function validMatchingMarketsJson(body: string | null | undefined): MatchingMarketsNewTradingType {
    if (body === undefined || body === null) {
        throw new Error('Matching markets validator => body is empty');
    }
    const jsonBody = JSON.parse(body);
    const matchingMarketsZodNew = TradingMarketBetLimitsLegacyViewZod.shape.matchingMarkets;

    return matchingMarketsZodNew.parse(jsonBody);
}

export async function buildGradeModel(
    newTradingModel: ApiSportGradesResponse200Type,
    legacyGrades: ApiSportGradesLegacyResponse200Type
): Promise<Array<GradeType>> {
    const outModel: Array<GradeType> = [];

    for (const grade of newTradingModel) {
        const legacyMarketBetLimits = legacyGrades.filter((legacyGrade) => legacyGrade.name === grade.name)[0];

        if (legacyMarketBetLimits === undefined) {
            throw new Error('legacyMarketBetLimits not found');
        } else {
            const convertedBetLimit: GradeType = {
                id: await encodeGradeForView(grade.name),
                name: grade.name,
                displayName: grade.displayName,
                displayOrder: 0,
                defaultLimits: grade.default,
                url: 'new-trading-response',
                marketLimits: buildMarketLimitsFromNewTrading(
                    grade.marketBetLimits,
                    legacyMarketBetLimits.marketBetLimits
                ),
            };

            outModel.push(convertedBetLimit);
        }
    }

    return outModel;
}

export function findBodyInLegacy(
    legacyGrades: ApiSportGradesLegacyResponse200Type,
    body: OpenapiProxyStaffUpdateSportGradesParamsType['requestBody']
): ApiSportGradesLegacyResponse200Type[number] | null {
    const marketBetLimitsMatched: Array<LegacyMarketLimitsType> = [];
    const marketBetLimitsFromLegacy = legacyGrades.find((grade) => grade.name === body.name);
    if (marketBetLimitsFromLegacy === undefined) {
        console.warn('Not found bet limits in legacy');
        return null;
    }

    for (const [market_limit_id, market_limit_body] of Object.entries(body.marketLimits)) {
        const group_default_limits = marketBetLimitsFromLegacy.default;
        const parsedMatchingMarkets = validMatchingMarketsJson(market_limit_body.matchingMarkets);
        const item: LegacyMarketLimitsType = {
            matchingMarkets: parsedMatchingMarkets,
            betLimits: {
                maxBet: market_limit_body.betLimits.maxBet ?? group_default_limits.maxBet,
                maxWin: market_limit_body.betLimits.maxWin ?? group_default_limits.maxWin,
                maxMultipleBet: market_limit_body.betLimits.maxMultipleBet ?? group_default_limits.maxMultipleBet,
                liabilityLimit: market_limit_body.betLimits.liabilityLimit ?? group_default_limits.liabilityLimit,
            },
            internalId: market_limit_id,
        };

        marketBetLimitsMatched.push(item);
    }

    marketBetLimitsFromLegacy.marketBetLimits =
        body.displayName !== undefined && body.displayName !== '' ? undefined : marketBetLimitsMatched;
    marketBetLimitsFromLegacy.displayName = body.displayName ?? marketBetLimitsFromLegacy.displayName;
    return marketBetLimitsFromLegacy;
}
